/**
 * Checkbox Component
 * Checkbox input with label support
 */

import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onCheckedChange?: (checked: boolean) => void;
  label?: string;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, onCheckedChange, label, ...props }, ref) =>
  {
    const checkbox = (
      <div className="relative inline-flex items-center">
        <input
          type="checkbox"
          className={cn(
            'peer h-4 w-4 shrink-0 rounded-sm border border-gray-300',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'checked:bg-blue-600 checked:border-blue-600',
            className,
          )}
          ref={ref}
          onChange={e => onCheckedChange?.(e.target.checked)}
          {...props}
        />
        <Check className="absolute left-0 h-4 w-4 text-white pointer-events-none opacity-0 peer-checked:opacity-100" />
      </div>
    );

    if (label)
    {
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          {checkbox}
          <span className="text-sm text-gray-700">{label}</span>
        </label>
      );
    }

    return checkbox;
  },
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
